import React from 'react'
import { useDeleteDonationMutation } from '../hooks'
import { useDonationsStore } from '../store/donationsStore'
import type { ApiError } from '@/types/api'
import { Modal } from '@/components/custom/modal'
import LoadingButton from '@/components/custom/loading-button'
import { useMembersStore } from '@/features/members/store/membersStore'
import { formatCurrency } from '@/utils/currencyUtils'
import { notifyRequestError } from '@/utils/notifyRequestError'
import { dateInToronto, formatDate } from '@/utils/timeZoneUtils'

export function DeleteDonation({
  id,
  trigger,
  open,
  onClose,
}: {
  id: string | null
  trigger?: React.ReactNode
  open?: boolean
  onClose?: () => void
}) {
  const { mutateAsync: deleteDonationMutation, isPending } =
    useDeleteDonationMutation()

  const donations = useDonationsStore((s) => s.donations)
  const donation = donations.find((u) => u._id === id)
  const members = useMembersStore((s) => s.members)
  const donor = members.find((m) => m._id === donation?.donorId)

  const handleDeleteDonation = async () => {
    if (!donation) return

    try {
      await deleteDonationMutation(donation._id)
      onClose?.()
    } catch (error) {
      notifyRequestError(error as ApiError, 'Failed to delete donation')
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      trigger={trigger}
      title="Delete Donation"
    >
      <div className="space-y-4">
        <p className="mb-3">Are you sure you want to delete this donation?</p>

        <div className="bg-gray-50 rounded-lg p-4 mb-4 space-y-2">
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div>
              <span className="font-medium text-gray-600">Receipt #:</span>
              <div className="font-semibold">
                {donation?.receiptNumber || 'N/A'}
              </div>
            </div>
            <div>
              <span className="font-medium text-gray-600">Amount:</span>
              <div className="font-semibold">
                {donation?.amount ? formatCurrency(donation.amount) : '$0.00'}
              </div>
            </div>
            <div>
              <span className="font-medium text-gray-600">Date:</span>
              <div className="font-semibold">
                {donation?.date
                  ? formatDate(dateInToronto(donation.date).toDate())
                  : 'N/A'}
              </div>
            </div>
            <div>
              <span className="font-medium text-gray-600">Donor:</span>
              <div className="font-semibold">{donor?.name || 'Unknown'}</div>
            </div>
          </div>
        </div>

        <p className="text-red-500 font-semibold text-sm">
          This action is permanent and cannot be undone.
        </p>

        <LoadingButton
          type="submit"
          className="w-full"
          isLoading={isPending}
          disabled={isPending}
          onClick={handleDeleteDonation}
        >
          {isPending ? 'Deleting...' : 'Delete'}
        </LoadingButton>
      </div>
    </Modal>
  )
}
